// SchedulePage — Site Visits & Schedule: month calendar + list views, recurrence engine, visit→inspection bridge.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ChevronLeft, ChevronRight, Plus, X, Save, Loader2, CalendarDays, List as ListIcon,
    CheckCircle2, ClipboardList, Pencil, Trash2, AlertTriangle, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { VisitDialog } from "@/components/visits/VisitFormDialog";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_TONE = { Scheduled: "blue", Completed: "green", Cancelled: "neutral" };
const RECURRENCE_TONE = "purple";

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

function visitColour(v) {
    if (v.status === "Completed") return "#3F7D3A";
    if (v.status === "Cancelled") return "#9A968F";
    if (v.scheduled_date < todayIso()) return "#B43A3A"; // overdue
    return "var(--brand-accent, #C75A1B)";
}

export default function SchedulePage() {
    const today = new Date();
    const [view, setView] = useState("calendar");
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [meta, setMeta] = useState({ visit_types: [], statuses: [], recurrence_options: [] });
    const [visits, setVisits] = useState([]);
    const [clientId, setClientId] = useState("");
    const [buildingId, setBuildingId] = useState("");
    const [visitType, setVisitType] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [selectedDay, setSelectedDay] = useState(null);
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [returnTo, setReturnTo] = useState(null);
    const [denied, setDenied] = useState(false);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const lockedClient = portalClientId && !isInternalView;

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
        api.get("/forms/templates", { params: { status: "published" } }).then((r) => setTemplates(r.data)).catch(() => {});
        api.get("/visits/meta").then((r) => setMeta(r.data)).catch(() => {});
    }, []);

    useEffect(() => { if (lockedClient) setClientId(portalClientId); }, [lockedClient, portalClientId]);

    const refresh = useCallback(async () => {
        try {
            const params = {};
            if (clientId) params.client_id = clientId;
            if (buildingId) params.building_id = buildingId;
            if (visitType) params.visit_type = visitType;
            if (statusFilter) params.status = statusFilter;
            if (view === "calendar") { params.year = year; params.month = month; }
            const { data } = await api.get("/visits", { params });
            setVisits(data);
            setDenied(false);
        } catch (e) {
            if (e.response?.status === 403) setDenied(true);
        }
    }, [view, year, month, clientId, buildingId, visitType, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            const prefill = {
                project_id: searchParams.get("project") || null,
                client_id: searchParams.get("client") || null,
                project_label: searchParams.get("project_label") || null,
            };
            setDialog({ open: true, mode: "create", initial: null, prefill });
            ["new", "project", "client", "project_label"].forEach((p) => searchParams.delete(p));
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams]);

    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId) {
            const back = searchParams.get("back");
            api.get(`/visits/${openId}`)
                .then((r) => { if (back) setReturnTo(back); setDialog({ open: true, mode: "edit", initial: r.data }); })
                .catch(() => toast.error("Could not open that site visit"));
            searchParams.delete("open");
            searchParams.delete("back");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams]);
    const buildingMap = Object.fromEntries(buildings.map((b) => [b.id, b]));
    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const filteredBuildings = buildings.filter((b) => !clientId || b.client_id === clientId);

    function changeMonth(dir) {
        let m = month + dir, y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y); setSelectedDay(null);
    }

    // calendar grid
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday-first
    const byDay = useMemo(() => {
        const m = {};
        visits.forEach((v) => { (m[v.scheduled_date] || (m[v.scheduled_date] = [])).push(v); });
        return m;
    }, [visits]);
    const grid = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const selectedDate = selectedDay ? iso(year, month, selectedDay) : null;
    const selectedVisits = selectedDate ? (byDay[selectedDate] || []) : [];

    async function completeVisit(v) {
        try {
            await api.post(`/visits/${v.id}/complete`);
            toast.success(`${v.title || v.visit_type} marked complete`);
            await refresh();
            // prompt to start the inspection (consultant chooses)
            if (window.confirm("Visit completed. Start the inspection form for this visit now?")) {
                await startInspection(v);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not complete");
        }
    }

    async function startInspection(v) {
        if (!v.template_id) {
            toast.error("Link an inspection template to this visit first (edit the visit).");
            return;
        }
        try {
            const { data } = await api.post(`/visits/${v.id}/create-inspection`, { template_id: v.template_id });
            toast.success("Inspection created");
            navigate(`/app/inspections/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start inspection");
        }
    }

    async function removeVisit(v) {
        if (!window.confirm(v.series_id ? "Delete this visit? (use the dialog to remove the whole series)" : "Delete this visit?")) return;
        try {
            await api.delete(`/visits/${v.id}`);
            toast.success("Visit deleted");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
        }
    }

    const overdueCount = visits.filter((v) => v.status === "Scheduled" && v.scheduled_date < todayIso()).length;

    return (
        <div data-testid="schedule-page" className="space-y-5">
            {denied ? (
                <div data-testid="schedule-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to Site Visits & Schedule.</p>
                </div>
            ) : (
            <>
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Projects & Delivery
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Site Visits & Schedule</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {visits.length} {view === "calendar" ? "this month" : "matching"} ·{" "}
                        <span className="font-semibold" style={{ color: overdueCount ? "#B43A3A" : "#6F6A64" }}>{overdueCount} overdue</span>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex rounded-[8px] border overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                        <button data-testid="schedule-view-calendar" onClick={() => setView("calendar")}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold ${view === "calendar" ? "bg-[#1F1F1F] text-white" : "bg-white text-[#2D2D2D]"}`}>
                            <CalendarDays className="w-3.5 h-3.5" /> Calendar
                        </button>
                        <button data-testid="schedule-view-list" onClick={() => setView("list")}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold ${view === "list" ? "bg-[#1F1F1F] text-white" : "bg-white text-[#2D2D2D]"}`}>
                            <ListIcon className="w-3.5 h-3.5" /> List
                        </button>
                    </div>
                    {canEdit && (
                        <button data-testid="schedule-new-button"
                            onClick={() => setDialog({ open: true, mode: "create", initial: null })}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            <Plus className="w-3.5 h-3.5" /> Schedule visit
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <select data-testid="schedule-client-filter" value={clientId} disabled={lockedClient}
                    onChange={(e) => { setClientId(e.target.value); setBuildingId(""); }}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All clients</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select data-testid="schedule-building-filter" value={buildingId} onChange={(e) => setBuildingId(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All buildings</option>
                    {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select data-testid="schedule-type-filter" value={visitType} onChange={(e) => setVisitType(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All types</option>
                    {meta.visit_types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select data-testid="schedule-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All statuses</option>
                    {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {view === "calendar" && (
                    <div className="ml-auto flex items-center gap-2">
                        <button data-testid="schedule-prev" onClick={() => changeMonth(-1)}
                            className="h-9 w-9 grid place-items-center rounded-lg border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]"><ChevronLeft size={16} /></button>
                        <div className="text-sm font-semibold text-[#1F1F1F] w-40 text-center" data-testid="schedule-month-label">
                            {new Date(year, month - 1, 1).toLocaleString("en-GB", { month: "long", year: "numeric" })}
                        </div>
                        <button data-testid="schedule-next" onClick={() => changeMonth(1)}
                            className="h-9 w-9 grid place-items-center rounded-lg border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]"><ChevronRight size={16} /></button>
                    </div>
                )}
            </div>

            {view === "calendar" ? (
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 rounded-2xl bg-white border border-[#E5E0DA] p-5">
                        <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-wider text-[#9A968F] font-bold">
                            {WEEKDAYS.map((w) => <div key={w} className="text-center py-1">{w}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1.5">
                            {grid.map((day, idx) => {
                                if (!day) return <div key={`pad-${idx}`} />;
                                const dateStr = iso(year, month, day);
                                const dv = byDay[dateStr] || [];
                                const isSelected = selectedDay === day;
                                const isToday = dateStr === todayIso();
                                return (
                                    <button key={day} data-testid={`schedule-day-${day}`}
                                        onClick={() => setSelectedDay(day)}
                                        className={`min-h-[68px] rounded-lg border p-1.5 text-left transition ${isSelected ? "ring-2 ring-[var(--brand-accent,_#C75A1B)]" : "hover:bg-[#F5F3F0]"}`}
                                        style={{ borderColor: isToday ? "var(--brand-accent, #C75A1B)" : "#F0EDE8", background: "#fff" }}>
                                        <div className="text-[11px] font-bold" style={{ color: isToday ? "var(--brand-accent, #C75A1B)" : "#6F6A64" }}>{day}</div>
                                        <div className="space-y-1 mt-1">
                                            {dv.slice(0, 3).map((v) => (
                                                <div key={v.id} className="text-[9.5px] truncate rounded px-1 py-0.5 text-white font-semibold"
                                                     style={{ background: visitColour(v) }}>{v.visit_type}</div>
                                            ))}
                                            {dv.length > 3 && <div className="text-[9px] text-[#9A968F]">+{dv.length - 3} more</div>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Day side panel */}
                    <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">
                            {selectedDate ? new Date(year, month - 1, selectedDay).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : "Select a day"}
                        </h4>
                        <div className="mt-3 space-y-2" data-testid="schedule-day-panel">
                            {!selectedDate && <div className="text-[12px] italic text-[#9A968F]">Click a day to see its visits.</div>}
                            {selectedDate && selectedVisits.length === 0 && <div className="text-[12px] italic text-[#9A968F]">No visits scheduled.</div>}
                            {selectedVisits.map((v) => (
                                <VisitRow key={v.id} v={v} buildingMap={buildingMap} canEdit={canEdit}
                                    onComplete={() => completeVisit(v)} onStart={() => startInspection(v)}
                                    onEdit={() => setDialog({ open: true, mode: "edit", initial: v })} onDelete={() => removeVisit(v)} />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F3F0] text-left text-[11px] uppercase tracking-wider text-[#6F6A64] border-b border-[#E5E0DA]">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Date</th>
                                <th className="px-4 py-3 font-semibold">Visit</th>
                                <th className="px-4 py-3 font-semibold">Client / Building</th>
                                <th className="px-4 py-3 font-semibold">Assignee</th>
                                <th className="px-4 py-3 font-semibold">Recurrence</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visits.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]">No visits match the filters.</td></tr>
                            )}
                            {visits.map((v) => {
                                const overdue = v.status === "Scheduled" && v.scheduled_date < todayIso();
                                return (
                                    <tr key={v.id} data-testid={`visit-row-${v.id}`} className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0]">
                                        <td className="px-4 py-3 font-mono text-[12px] text-[#2D2D2D]">
                                            <div className="flex items-center gap-1.5">{overdue && <AlertTriangle size={13} className="text-red-600" />}{v.scheduled_date}</div>
                                        </td>
                                        <td className="px-4 py-3"><div className="font-semibold text-[#1F1F1F]">{v.title || v.visit_type}</div><div className="text-[11px] font-mono text-[#9A968F]">{v.visit_type} · {v.reference}</div></td>
                                        <td className="px-4 py-3 text-[#6F6A64]"><div>{clientMap[v.client_id]?.name || "—"}</div>{v.building_id && <div className="text-[11px]">{buildingMap[v.building_id]?.name || ""}</div>}</td>
                                        <td className="px-4 py-3 text-[#6F6A64]">{v.assignee_name || "—"}</td>
                                        <td className="px-4 py-3">{v.recurrence !== "None" ? <Pill tone={RECURRENCE_TONE} icon={Repeat}>{v.recurrence}</Pill> : <span className="text-[#9A968F]">—</span>}</td>
                                        <td className="px-4 py-3"><Pill tone={overdue ? "red" : STATUS_TONE[v.status] || "neutral"}>{overdue ? "Overdue" : v.status}</Pill></td>
                                        <td className="px-4 py-3 text-right">
                                            {canEdit && (
                                                <div className="inline-flex items-center gap-1">
                                                    {v.status !== "Completed" && (
                                                        <button data-testid={`visit-complete-${v.id}`} title="Complete" onClick={() => completeVisit(v)}
                                                            className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#E8F1E6] text-[#3F7D3A]"><CheckCircle2 className="w-4 h-4" /></button>
                                                    )}
                                                    <button data-testid={`visit-start-inspection-${v.id}`} title="Start inspection" onClick={() => startInspection(v)}
                                                        className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#F6F4F0] text-[#2A5DB0]"><ClipboardList className="w-4 h-4" /></button>
                                                    <button data-testid={`visit-edit-${v.id}`} title="Edit" onClick={() => setDialog({ open: true, mode: "edit", initial: v })}
                                                        className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#F6F4F0]"><Pencil className="w-4 h-4" style={{ color: "#6F6A64" }} /></button>
                                                    <button data-testid={`visit-delete-${v.id}`} title="Delete" onClick={() => removeVisit(v)}
                                                        className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#FBE8E5] text-[#B33A2E]"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <VisitDialog open={dialog.open} mode={dialog.mode} initial={dialog.initial} prefill={dialog.prefill}
                clients={clients} buildings={buildings} templates={templates} meta={meta}
                lockedClientId={lockedClient ? portalClientId : null}
                onClose={() => { setDialog((d) => ({ ...d, open: false })); if (returnTo) { const t = returnTo; setReturnTo(null); navigate(t); } }} onSaved={refresh} />
            </>
            )}
        </div>
    );
}

function VisitRow({ v, buildingMap, canEdit, onComplete, onStart, onEdit, onDelete }) {
    const overdue = v.status === "Scheduled" && v.scheduled_date < todayIso();
    return (
        <div data-testid={`schedule-visit-${v.id}`} className="rounded-[9px] border p-2.5" style={{ borderColor: "#E5E0DA", borderLeft: `3px solid ${visitColour(v)}` }}>
            <div className="flex items-center justify-between">
                <div className="font-semibold text-[12.5px] text-[#1F1F1F]">{v.visit_type}</div>
                <Pill tone={overdue ? "red" : STATUS_TONE[v.status] || "neutral"}>{overdue ? "Overdue" : v.status}</Pill>
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "#6F6A64" }}>
                {v.assignee_name || "Unassigned"}{v.building_id ? ` · ${buildingMap[v.building_id]?.name || ""}` : ""}
                {v.recurrence !== "None" ? ` · ${v.recurrence}` : ""}
            </div>
            {canEdit && (
                <div className="flex items-center gap-1 mt-2">
                    {v.status !== "Completed" && (
                        <button data-testid={`visit-complete-${v.id}`} onClick={onComplete}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-[6px] bg-[#E8F1E6] text-[#3F7D3A]"><CheckCircle2 className="w-3 h-3" /> Complete</button>
                    )}
                    <button data-testid={`visit-start-inspection-${v.id}`} onClick={onStart}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-[6px] bg-[#E7EEF9] text-[#2A5DB0]"><ClipboardList className="w-3 h-3" /> Inspection</button>
                    <button data-testid={`visit-edit-${v.id}`} onClick={onEdit} className="w-7 h-7 grid place-items-center rounded-[6px] hover:bg-[#F6F4F0]"><Pencil className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /></button>
                    <button data-testid={`visit-delete-${v.id}`} onClick={onDelete} className="w-7 h-7 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5] text-[#B33A2E]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
            )}
        </div>
    );
}


